import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { Alert, Avatar, Dialog, DialogContent, Grid, IconButton, ImageList, ImageListItem, Paper, Tab, Tabs, Typography } from '@mui/material';
import { blue } from '@mui/material/colors';
import { PackageImage, TakeImagePhase } from 'adoms-common-lib';
import React, { useEffect } from 'react';
import { APIConnector } from '../../connector/APIConnector';

type PackageImageDisplayPaperProps = {
    tenantId?: string
    orderId?: string
};

const TAKE_IMAGE_PHASE_ITEMS: { phase: TakeImagePhase, label: string }[] = [
    { phase: TakeImagePhase.Received, label: '受領時' },
    { phase: TakeImagePhase.Packed, label: '梱包時' },
    { phase: TakeImagePhase.Loaded, label: '搭載時' },
];

const createEmptyPhaseImageState = (): Record<TakeImagePhase, PackageImage[]> => ({
    [TakeImagePhase.Received]: [],
    [TakeImagePhase.Packed]: [],
    [TakeImagePhase.Loaded]: []
});

const toImageSrc = (imageBase64: string): string => {
    if (imageBase64.startsWith('data:image')) {
        return imageBase64;
    }
    return `data:image/jpeg;base64,${imageBase64}`;
};

export const PackageImageDisplayPaper: React.FC<PackageImageDisplayPaperProps> = (props) => {
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [error, setError] = React.useState<string>();
    const [selectedPhase, setSelectedPhase] = React.useState<TakeImagePhase>(TakeImagePhase.Received);
    const [imagesByPhase, setImagesByPhase] = React.useState<Record<TakeImagePhase, PackageImage[]>>(createEmptyPhaseImageState());
    const [selectedImage, setSelectedImage] = React.useState<{ src: string, alt: string }>();

    useEffect(() => {
        const fetchPackageImages = async () => {
            if (!props.orderId || !props.tenantId) {
                return;
            }

            const orderId = props.orderId;
            const tenantId = props.tenantId;

            setIsLoading(true);
            setError(undefined);

            const apiConnector: APIConnector = APIConnector.instance;
            try {
                const results = await Promise.all(
                    TAKE_IMAGE_PHASE_ITEMS.map(async ({ phase }) => {
                        const images = await apiConnector.getPackageImage(tenantId, orderId, phase);
                        return { phase, images };
                    })
                );

                const nextImagesByPhase = createEmptyPhaseImageState();
                results.forEach(({ phase, images }) => {
                    nextImagesByPhase[phase] = images;
                });
                setImagesByPhase(nextImagesByPhase);
            } catch (e) {
                console.log(e);
                setError('荷物画像を取得できませんでした。');
                setImagesByPhase(createEmptyPhaseImageState());
            } finally {
                setIsLoading(false);
            }
        };

        fetchPackageImages();
    }, [props.orderId, props.tenantId]);

    return (
        <Paper sx={{
            borderRadius: '10px',
            margin: '0px 10px',
            padding: '10px'
        }} elevation={2}>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item>
                            <Avatar sx={{ bgcolor: blue[400], width: 36, height: 36 }}>
                                <AddPhotoAlternateIcon fontSize='small' />
                            </Avatar>
                        </Grid>
                        <Grid item xs>
                            <Typography variant="subtitle1" component="div">
                                荷物外観写真
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
                {error ?
                    <Grid item xs={12}>
                        <Alert severity="error">{error}</Alert>
                    </Grid>
                    : null}

                <Grid item xs={12}>
                    <Tabs
                        value={selectedPhase}
                        onChange={(_, value: TakeImagePhase) => setSelectedPhase(value)}
                        variant="fullWidth"
                    >
                        {TAKE_IMAGE_PHASE_ITEMS.map((phaseItem) => (
                            <Tab
                                key={phaseItem.phase}
                                value={phaseItem.phase}
                                label={phaseItem.label}
                            />
                        ))}
                    </Tabs>
                </Grid>

                {isLoading ?
                    <Grid item xs={12}>
                        <Typography variant="body2" color="textSecondary">
                            荷物画像を読み込み中です。
                        </Typography>
                    </Grid>
                    : (
                        <Grid item xs={12}>
                            {TAKE_IMAGE_PHASE_ITEMS.map((phaseItem) => {
                                const phaseImages = imagesByPhase[phaseItem.phase] ?? [];

                                return (
                                    <Grid
                                        key={phaseItem.phase}
                                        sx={{ display: selectedPhase === phaseItem.phase ? 'block' : 'none' }}
                                    >
                                        {phaseImages.length > 0 ?
                                            <ImageList
                                                cols={4}
                                                gap={8}
                                                sx={{
                                                    m: 0,
                                                    gridTemplateColumns: {
                                                        xs: 'repeat(2, 1fr) !important',
                                                        sm: 'repeat(3, 1fr) !important',
                                                        md: 'repeat(4, 1fr) !important'
                                                    }
                                                }}
                                            >
                                                {phaseImages.map((image, index) => (
                                                    <ImageListItem key={`${phaseItem.phase}-${index}`}>
                                                        <img
                                                            src={toImageSrc(image.imageBase64)}
                                                            alt={`${phaseItem.label}画像${index + 1}`}
                                                            loading="eager"
                                                            style={{ width: '100%', borderRadius: '4px', cursor: 'pointer', display: 'block' }}
                                                            onClick={() => setSelectedImage({
                                                                src: toImageSrc(image.imageBase64),
                                                                alt: `${phaseItem.label}画像${index + 1}`
                                                            })}
                                                        />
                                                    </ImageListItem>
                                                ))}
                                            </ImageList>
                                            :
                                            <Typography variant="body2" color="textSecondary" margin={1}>
                                                画像はありません。
                                            </Typography>
                                        }
                                    </Grid>
                                );
                            })}
                        </Grid>
                    )
                }
            </Grid>
            <Dialog
                open={Boolean(selectedImage)}
                onClose={() => setSelectedImage(undefined)}
                maxWidth="lg"
                fullWidth
            >
                <DialogContent sx={{ p: 1 }}>
                    <Grid container justifyContent="flex-end">
                        <IconButton onClick={() => setSelectedImage(undefined)} aria-label="閉じる" size="small">
                            <CloseIcon />
                        </IconButton>
                    </Grid>
                    {selectedImage ?
                        <img
                            src={selectedImage.src}
                            alt={selectedImage.alt}
                            style={{ width: '100%', height: 'auto', display: 'block' }}
                        />
                        : null}
                </DialogContent>
            </Dialog>
        </Paper>
    );
};
